import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { Crown, Check, Calendar, CheckCircle, CreditCard, Shield, Clock, MessageCircle, RefreshCw, ExternalLink, CalendarClock, QrCode, MessageSquare, Sparkles, Users, BarChart3, Building2, Download, Headset, Wallet, FileText, Stethoscope, CalendarDays, GraduationCap, Receipt } from 'lucide-react';
import DateTimeDisplay from '../../../components/common/DateTimeDisplay';
import BrandLoader from '../../../components/common/BrandLoader';
import AlertBox from '../../../components/ui/AlertBox';
import MpPayerEmailField from '../components/MpPayerEmailField';
import { useError } from '../../../context/ErrorContext';
import PaymentController from '../services/PaymentController';
import { billingApi, periodSuffixOf, chargedEveryOf, perPeriodOf, type PlanFeatureRich, type PlanDueSoon, type OwnerAccount, type PlanOption, type AutopayStatus } from '../../../api/billingApi';

// Nombre genérico de ícono (lo manda el servidor con cada beneficio) → ícono real.
const FEATURE_ICONS: Record<string, React.ElementType> = {
  qr: QrCode, message: MessageSquare, sparkles: Sparkles, users: Users, chart: BarChart3,
  branches: Building2, export: Download, headset: Headset, wallet: Wallet, file: FileText,
  stethoscope: Stethoscope, calendar: CalendarDays, graduation: GraduationCap, receipt: Receipt,
};
import { isPro as checkIsPro, isPremium as checkIsPremium, getPlanLabel } from '../../../utils/planPermissions';

const WHATSAPP_NUMBER = '526861326204';
const WHATSAPP_URL = 'https://wa.me/' + WHATSAPP_NUMBER;
const DISMISSED_PAYMENTS_KEY = 'lealify_dismissed_payment_banners';

// ── QUÉ SE LE PROMETE A CADA QUIEN (F1, 13-ago-2026) ─────────────────────────
// Esta pantalla la ve TODO cliente: el módulo `billing` es core. Hasta hoy la lista era una
// sola, escrita cuando lealtad era el único producto — así que a una clínica que paga por su
// agenda le hablaba de códigos QR publicitarios y de cumpleañeros, y ni una palabra de
// odontograma o recetas. Ahora cada vertical lee lo suyo.
const FREE_FEATURES = [
  'Hasta 1,000 clientes registrados',
  '1 sucursal incluida',
  'Gestión básica de clientes',
  'Sistema de puntos',
  'Recompensas sin límites',
  'Reportes básicos'
];

// Sin plan vigente en una vertical: lo que SÍ conserva (ver F2, solo lectura). No se le
// prometen puntos ni recompensas — eso es de lealtad y una clínica no lo tiene.
const FREE_FEATURES_VERTICAL = [
  'Consulta de toda tu información: agenda, historial y expedientes',
  'Tus datos siguen intactos y siempre disponibles',
  'Puedes exportar y consultar tus reportes',
  'No se registran citas, cobros ni movimientos nuevos',
];

// Lo que incluye el plan, POR VERTICAL. Redactado desde lo que el negocio de verdad usa
// (medido en `tenant_modules`), no desde el catálogo de funciones.
const PRO_FEATURES_POR_VERTICAL: Record<string, string[]> = {
  dental: [
    'Agenda de citas con recordatorios automáticos por WhatsApp',
    'Expediente clínico por paciente (historial, alergias, medicación)',
    'Odontograma con seguimiento de tratamientos',
    'Recetas y consentimientos informados listos para imprimir',
    'Facturación y constancias fiscales',
    'Cobros, caja y estado de cuenta por paciente',
    'Recordatorio de cumpleaños a tus pacientes',
    'Soporte directo por WhatsApp',
  ],
  letsmove: [
    'Clases, horarios y control de asistencias',
    'Alumnas, membresías y planes por familia',
    'Cobranza mensual con adeudos y recargos',
    'Nómina de profesores por clases impartidas',
    'Mensajes automáticos a las mamás por WhatsApp',
    'Reportes de ingresos, asistencia y morosidad',
    'Soporte directo por WhatsApp',
  ],
  clublectura: [
    'Votaciones y propuestas de libros',
    'Sedes y calendario de reuniones',
    'Miembros y asistencia',
    'Soporte directo por WhatsApp',
  ],
  pos: [
    'Punto de venta con corte de caja',
    'Catálogo de productos e inventario',
    'Ventas por turno y arqueo',
    'Comandas conectadas a WhatsApp',
    'Soporte directo por WhatsApp',
  ],
};

// Lealtad (el producto original) y fallback para cualquier vertical sin lista propia.
const PRO_FEATURES = [
  'Clientes sin límite (el plan gratis admite hasta 1,000 registrados)',
  'Sucursales adicionales con precio escalonado (entre más sucursales, más barata cada una)',
  'Reportes avanzados y analíticas (toma decisiones basadas en datos reales)',
  'Personalización avanzada (adapta el sistema a tus necesidades específicas)',
  'Personalización de QR para publicidad (refuerza tu marca en cada interacción)',
  'Exportación de datos (integra con otras herramientas de tu negocio)',
  'Envío de mensajes por WhatsApp (comunícate directamente con plantillas prediseñadas)',
  'Capacidad de ver clientes inactivos (identifica oportunidades de recuperación)',
  'Ver cumpleañeros del día y del mes (crea momentos especiales de conexión)',
  'Mensajes personalizados de cumpleaños (sorprende a tus clientes en su día especial)',
  'Y otras características exclusivas (maximiza el potencial de tu negocio)'
];

interface FeatureListProps {
  features: string[];
  iconColor: string;
  Icon?: React.ElementType;
}

function FeatureList({ features, iconColor, Icon = Check }: FeatureListProps) {
  return (
    <ul className="space-y-3">
      {features.map(function(feature, index) {
        return (
          <li key={index} className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <Icon className={'h-5 w-5 ' + iconColor + ' mr-2'} aria-hidden="true" />
            </div>
            <span className="text-sm">{feature}</span>
          </li>
        );
      })}
    </ul>
  );
}

interface PendingPayment {
  id: string;
  status: 'pending' | 'failed' | 'completed';
  amount: number;
  currency: string;
  createdAt: Date;
  planName: string;
  method?: string;
  checkoutLink?: string;
}

function getDismissedPayments(): string[] {
  try {
    const stored = localStorage.getItem(DISMISSED_PAYMENTS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    return [];
  }
}

function addDismissedPayment(paymentId: string): void {
  try {
    const dismissed = getDismissedPayments();
    if (!dismissed.includes(paymentId)) {
      dismissed.push(paymentId);
      localStorage.setItem(DISMISSED_PAYMENTS_KEY, JSON.stringify(dismissed));
    }
  } catch (error) {
    console.error('Error saving dismissed payment:', error);
  }
}

function isPaymentDismissed(paymentId: string): boolean {
  return getDismissedPayments().includes(paymentId);
}

function SubscriptionPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { window.gtag?.('event', 'paywall_view'); }, []);
  const { addError, clearErrors } = useError();
  const [searchParams] = useSearchParams();

  // Regreso del checkout (BTCPay/Mercado Pago) con ...?paid=1: aviso de confirmación.
  const justPaid = searchParams.get('paid') === '1';
  // Regreso de Mercado Pago con pago fallido/rechazado: ...?payfail=1
  const justFailed = searchParams.get('payfail') === '1';

  const [recentPayment, setRecentPayment] = useState<PendingPayment | null>(null);
  // Precio REAL del servidor (acordado si lo hay, si no la tarifa por sucursales) y su
  // periodicidad. NO se recalcula aquí: hacerlo enseñaba $2,500/año a clientes que tienen
  // precio negociado ($5,000 anuales, $500 al mes...) y el cobro luego generaba otra cifra.
  const [precio, setPrecio] = useState<{ monto: number; meses: number } | null>(null);
  // Beneficios de SU vertical (dental, letsmove…): manda la lista del SERVIDOR
  // (plan_features de /billing/subscription, fuente única desde fase A 18-ago-2026);
  // mientras carga —o si el backend aún no la manda— la copia local de siempre.
  const [apiFeatures, setApiFeatures] = useState<string[] | null>(null);
  // Versión RICA de los beneficios (ícono+título+desc) + estado vivo del plan (plan L, 18-ago-2026).
  const [featuresRich, setFeaturesRich] = useState<PlanFeatureRich[] | null>(null);
  const [dueSoon, setDueSoon] = useState<PlanDueSoon | null>(null);
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const [esCortesia, setEsCortesia] = useState(false);
  // Periodos elegibles + domiciliación (27-sep-2026): del servidor, fuente única.
  const [planOptions, setPlanOptions] = useState<PlanOption[]>([]);
  const [planCodeActual, setPlanCodeActual] = useState<string>('free');
  const [autopay, setAutopay] = useState<AutopayStatus | null>(null);
  const [autopayBusy, setAutopayBusy] = useState(false);
  const [autopayErr, setAutopayErr] = useState<string | null>(null);
  const [autopayAskEmail, setAutopayAskEmail] = useState(false);   // paso «correo de MP» antes de ir a MP
  const [historial, setHistorial] = useState<NonNullable<OwnerAccount['paymentsAll']>>([]);
  const vertical = currentUser?.vertical || 'lealtad';
  const esVerticalDePago = vertical !== 'lealtad';
  const proFeatures = apiFeatures || PRO_FEATURES_POR_VERTICAL[vertical] || PRO_FEATURES;
  // En una vertical, "sin plan" NO es un plan gratis con puntos y recompensas: es el modo
  // consulta que deja F2 (ve todo, no registra). Decirle "sistema de puntos" a una clínica
  // sería prometerle algo que no tiene.
  const freeFeatures = esVerticalDePago ? FREE_FEATURES_VERTICAL : FREE_FEATURES;
  const [loadingPayment, setLoadingPayment] = useState(true);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // hasProAccess = true si es pro O premium (tiene funcionalidades desbloqueadas)
  const hasProAccess = checkIsPro(currentUser?.subscription);
  // hasPremiumAccess = true SOLO si es premium
  const hasPremiumAccess = checkIsPremium(currentUser?.subscription);
  // Label dinámico del plan actual
  const currentPlanLabel = getPlanLabel(currentUser?.subscription?.plan || 'free');

  useEffect(function() {
    async function fetchRecentPayment() {
      if (!currentUser?.uid) {
        setLoadingPayment(false);
        return;
      }

      try {
        const cuenta = await billingApi.getAccount();
        const { lastPayment } = cuenta;
        if (cuenta.price > 0) setPrecio({ monto: cuenta.price, meses: cuenta.planMonths });
        if (cuenta.planFeatures) setApiFeatures(cuenta.planFeatures);
        if (cuenta.planFeaturesRich) setFeaturesRich(cuenta.planFeaturesRich);
        setDueSoon(cuenta.planDueSoon || null);
        setDaysLeft(cuenta.daysLeft ?? null);
        setEsCortesia(!!cuenta.isTrial);
        setHistorial(cuenta.paymentsAll || []);
        setPlanOptions(cuenta.planOptions || []);
        setPlanCodeActual(cuenta.planCode || 'free');
        setAutopay(cuenta.autopayStatus || null);

        if (lastPayment) {
          const createdAt = lastPayment.createdAt || new Date();
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

          if (lastPayment.status === 'pending' || (lastPayment.status === 'failed' && createdAt > sevenDaysAgo)) {
            setRecentPayment({
              id: lastPayment.id,
              status: lastPayment.status,
              amount: lastPayment.amount,
              currency: lastPayment.currency || 'MXN',
              createdAt: createdAt,
              planName: lastPayment.planName || 'Plan PRO',
              method: lastPayment.method,
              checkoutLink: lastPayment.checkoutLink
            });
            setBannerDismissed(isPaymentDismissed(lastPayment.id));
          }
        }
      } catch (error) {
        console.error('Error fetching recent payment:', error);
      } finally {
        setLoadingPayment(false);
      }
    }

    fetchRecentPayment();
  }, [currentUser?.uid]);

  useEffect(function() {
    if (clearErrors) clearErrors();
    if (currentUser?.subscription?.status === 'expired' && 
        (currentUser?.subscription?.plan === 'pro' || currentUser?.subscription?.plan === 'premium')) {
      if (addError) {
        addError(
          'Tu suscripción ha expirado. Algunas funcionalidades no estarán disponibles.',
          'validation',
          'subscription-expired'
        );
      }
    }
  }, [currentUser?.subscription?.status, currentUser?.subscription?.plan, addError, clearErrors]);

  function handleSubscribe() {
    if (clearErrors) clearErrors();
    // Preselecciona su periodo vigente; desde GRATIS, el MENSUAL (11-oct-2026): la tarjeta dice
    // «desde $208 al mes» y antes llegaba al pago con el anual de $2,500 elegido. En la pantalla
    // de pago puede cambiarlo a semestral / anual (allí se le enseña el ahorro del anual).
    const destino = planCodeActual !== 'free' ? planCodeActual : 'pro_monthly';
    window.gtag?.('event', 'subscribe_click', { from_plan: planCodeActual, plan: destino });
    navigate('/dashboard/subscription/payment', {
      state: {
        plan: PaymentController.planById(destino)
      }
    });
  }

  // Domiciliación (Suscripciones MP): activar manda al checkout de MP; apagar no toca el plan.
  async function handleAutopayStart(payerEmail: string) {
    setAutopayBusy(true); setAutopayErr(null);
    try { const r = await billingApi.startAutopay(planCodeActual !== 'free' ? planCodeActual : null, payerEmail); window.location.href = r.checkoutUrl; }
    catch (e) { setAutopayErr(e instanceof Error ? e.message : 'No se pudo iniciar el pago automático'); setAutopayBusy(false); }
  }
  async function handleAutopayCancel() {
    if (!window.confirm('¿Apagar el pago automático? Tu plan vigente no se pierde; volverás a renovar manualmente.')) return;
    setAutopayBusy(true); setAutopayErr(null);
    try { await billingApi.cancelAutopay(); setAutopay('cancelled'); }
    catch (e) { setAutopayErr(e instanceof Error ? e.message : 'No se pudo apagar el pago automático'); }
    setAutopayBusy(false);
  }
  const opcionMensual = planOptions.find((o) => o.code === 'pro_monthly');

  function handleDismissBanner() {
    if (recentPayment) {
      addDismissedPayment(recentPayment.id);
    }
    setBannerDismissed(true);
  }

  function getWhatsAppLink(message: string) {
    return WHATSAPP_URL + '?text=' + encodeURIComponent(message);
  }

  const hasPendingPayment = recentPayment?.status === 'pending' && !bannerDismissed;

  if (loadingPayment) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-12">
          <BrandLoader size={36} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0">
        <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
            <Crown className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-yellow-500 animate-pulse" />
            Suscripción
          </h1>
          <p className="mt-1 text-sm text-content-2">
            Gestiona tu plan de suscripción y accede a más funcionalidades
          </p>
        </div>

        {/* Regreso del checkout BTCPay (?paid=1): el webhook aún no confirma → aviso "en proceso" */}
        {justPaid && !hasProAccess && (
          <AlertBox
            variant="info"
            icon={Clock}
            iconClassName="animate-pulse"
            title="Estamos confirmando tu pago"
            className="shadow animate-fade-in-up"
            style={{ animationDelay: '0.15s' }}
          >
            <p>
              Tu pago se está procesando en la red. Tu plan PRO se activará <strong>automáticamente</strong> en
              cuanto se confirme (normalmente unos minutos). No necesitas enviar comprobante.
            </p>
            <div className="mt-3">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-accent-solid hover:bg-accent-solid/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                Actualizar estado
              </button>
            </div>
          </AlertBox>
        )}

        {/* Regreso del checkout con la suscripción ya activa (webhook fue rápido) */}
        {justPaid && hasProAccess && (
          <AlertBox
            variant="success"
            title="¡Pago confirmado!"
            className="shadow animate-fade-in-up"
            style={{ animationDelay: '0.15s' }}
          >
            <p>Tu plan PRO ya está activo. ¡Gracias!</p>
          </AlertBox>
        )}

        {/* Regreso de Mercado Pago con pago no completado (?payfail=1) */}
        {justFailed && !hasProAccess && (
          <AlertBox
            variant="error"
            title="El pago no se completó"
            className="shadow animate-fade-in-up"
            style={{ animationDelay: '0.15s' }}
          >
            <p>
              Tu pago fue rechazado o se canceló y no se realizó ningún cargo. Puedes intentar de nuevo con otro método.
            </p>
            <div className="mt-3">
              <button
                onClick={handleSubscribe}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-accent-solid hover:bg-accent-solid/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              >
                Intentar de nuevo
              </button>
            </div>
          </AlertBox>
        )}

        {!justPaid && !justFailed && !bannerDismissed && recentPayment && !hasProAccess && recentPayment.status === 'pending' && (
          <AlertBox
            variant="info"
            icon={Clock}
            title={recentPayment.method === 'manual' ? 'Tu pago está en revisión' : 'Tu pago se está confirmando'}
            onDismiss={handleDismissBanner}
            className="shadow animate-fade-in-up"
            style={{ animationDelay: '0.15s' }}
          >
            {recentPayment.method === 'btcpay' ? (
              <p>
                Generaste un cobro con criptomonedas el <DateTimeDisplay date={recentPayment.createdAt} format="dateOnly" className="font-medium" />.
                Tu plan PRO se activará automáticamente al confirmarse en la red. Si no completaste el pago, puedes retomar el checkout.
              </p>
            ) : recentPayment.method === 'mercadopago' ? (
              <p>
                Generaste un cobro con Mercado Pago el <DateTimeDisplay date={recentPayment.createdAt} format="dateOnly" className="font-medium" />.
                Tu plan PRO se activará automáticamente al confirmarse el pago (inmediato con tarjeta; OXXO/SPEI puede tardar unas horas). Si no lo completaste, puedes retomar el pago.
              </p>
            ) : (
              <p>
                Recibimos tu comprobante el <DateTimeDisplay date={recentPayment.createdAt} format="dateOnly" className="font-medium" />.
                Te notificaremos cuando sea verificado (máximo 24 horas hábiles).
              </p>
            )}
            <p className="mt-1 text-xs text-accent">
              Monto: {recentPayment.currency} ${recentPayment.amount.toLocaleString('es-MX')}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(recentPayment.method === 'btcpay' || recentPayment.method === 'mercadopago') && recentPayment.checkoutLink && (
                <a href={recentPayment.checkoutLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-accent-solid hover:bg-accent-solid/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Retomar pago
                </a>
              )}
              <a href={getWhatsAppLink('Hola, tengo una consulta sobre mi pago en Lealify. ID de pago: ' + recentPayment.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                ¿Dudas? Escríbenos por WhatsApp
              </a>
            </div>
          </AlertBox>
        )}

        {!bannerDismissed && recentPayment && !hasProAccess && recentPayment.status === 'failed' && (
          <AlertBox
            variant="error"
            title="Pago no verificado"
            onDismiss={handleDismissBanner}
            className="shadow animate-fade-in-up"
            style={{ animationDelay: '0.15s' }}
          >
            <p>
              Tu pago del <DateTimeDisplay date={recentPayment.createdAt} format="dateOnly" className="font-medium" /> no pudo ser verificado.
              Por favor, intenta nuevamente o contáctanos si crees que es un error.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={handleSubscribe}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-500/20 hover:bg-red-200 dark:hover:bg-red-500/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Intentar de nuevo
              </button>
              <a href={getWhatsAppLink('Hola, mi pago en Lealify fue rechazado y quisiera más información. ID de pago: ' + recentPayment.id)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                Contactar por WhatsApp
              </a>
            </div>
          </AlertBox>
        )}

        {/* Por vencer (plan L1): mismo umbral que el correo del cron (5 días mensual / 7 anual),
            calculado por el servidor. La cortesía no recibe correos: este banner es su aviso. */}
        {dueSoon && !hasPendingPayment && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-500/40 dark:bg-amber-500/10 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-2">
                <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div>
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                    {dueSoon.is_trial ? 'Tu periodo de cortesía' : 'Tu plan'} {dueSoon.days_left <= 1 ? 'vence mañana' : `vence en ${dueSoon.days_left} días`}
                  </p>
                  <p className="mt-0.5 text-sm text-amber-800 dark:text-amber-300/90">
                    Renueva antes para no perder campañas, sucursales adicionales y reportes.
                  </p>
                </div>
              </div>
              <button
                onClick={handleSubscribe}
                className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
              >
                Renovar ahora
              </button>
            </div>
          </div>
        )}

        {hasProAccess && (
          <div className="bg-surface shadow rounded-lg p-4 sm:p-6 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <h2 className="text-lg font-medium text-content flex items-center">
                  <Crown className="h-5 w-5 mr-2 text-yellow-500" />
                  Plan {currentPlanLabel} Activo
                  {esCortesia && (
                    <span className="ml-2 rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">
                      de cortesía
                    </span>
                  )}
                </h2>
                <p className="mt-1 text-sm text-content-2">
                  {precio
                    ? <>Pagas <span className="font-semibold text-content">${precio.monto.toLocaleString('es-MX')} MXN</span> {perPeriodOf(precio.meses)} · acceso a todas las funcionalidades {hasPremiumAccess ? 'premium' : 'pro'}</>
                    : <>Tienes acceso a todas las funcionalidades {hasPremiumAccess ? 'premium' : 'pro'}</>}
                </p>
              </div>
              <div className="flex items-center gap-4 mt-2 sm:mt-0">
                <div className="text-left sm:text-right">
                  <div className="text-sm text-content-3 flex items-center sm:justify-end">
                    <Calendar className="h-4 w-4 mr-1" />
                    Vigente hasta:
                  </div>
                  <DateTimeDisplay
                    date={currentUser?.subscription?.endDate}
                    format="dateOnly"
                    className="text-sm font-medium text-content"
                  />
                  {daysLeft != null && daysLeft >= 0 && (
                    <div className="text-xs text-content-3">{daysLeft === 1 ? 'queda 1 día' : `quedan ${daysLeft} días`}</div>
                  )}
                </div>
                {/* Renovar desde AQUÍ (plan L1): antes un PRO tenía que bajar a la tarjeta azul
                    de venta, cuyo botón además está deshabilitado para él. Se suma a su vigencia. */}
                {!hasPendingPayment && (
                  <button
                    onClick={handleSubscribe}
                    className="shrink-0 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm font-medium text-content hover:bg-canvas"
                    title="El nuevo periodo se suma a tu vigencia actual"
                  >
                    Renovar ahora
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pago automático (domiciliación, 27-sep-2026 en el manager; en la agenda desde 30-ago):
            autoriza la tarjeta UNA vez y el plan se cobra solo cada ciclo. Solo con plan de pago
            real (no cortesía, monto > 0). Apagarlo no toca el plan vigente. */}
        {hasProAccess && !esCortesia && precio && precio.monto > 0 && (
          <div className="bg-surface shadow rounded-lg p-4 sm:p-6 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <h2 className="text-lg font-medium text-content flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-emerald-600" /> Pago automático
              {autopay === 'authorized' && (
                <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">ACTIVO</span>
              )}
            </h2>
            {autopay === 'authorized' ? (
              <>
                <p className="mt-1 text-sm text-content-2">
                  ${precio.monto.toLocaleString('es-MX')} MXN {perPeriodOf(precio.meses)} se cobra solo a tu tarjeta vía Mercado Pago. Sin recordatorios ni comprobantes.
                </p>
                <button type="button" disabled={autopayBusy} onClick={handleAutopayCancel}
                  className="mt-3 rounded-md border border-line bg-surface-2 px-4 py-2 text-sm font-medium text-content hover:bg-canvas disabled:opacity-60">
                  Apagar pago automático
                </button>
              </>
            ) : (
              <>
                <p className="mt-1 text-sm text-content-2">
                  Domicilia tu pago y olvídate de renovar: autorizas tu tarjeta una sola vez y ${precio.monto.toLocaleString('es-MX')} MXN {perPeriodOf(precio.meses)} se cobra solo.
                  {currentUser?.subscription?.endDate && daysLeft != null && daysLeft > 0 && ' El primer cargo será hasta tu próximo vencimiento: no pagas doble.'}
                  {' '}Lo puedes apagar aquí cuando quieras.
                </p>
                {autopayAskEmail ? (
                  <MpPayerEmailField defaultEmail={currentUser?.email || ''} busy={autopayBusy}
                    onConfirm={handleAutopayStart} onCancel={() => { setAutopayAskEmail(false); setAutopayErr(null); }} />
                ) : (
                  <button type="button" data-testid="btn-autopay-start" disabled={autopayBusy} onClick={() => { setAutopayErr(null); setAutopayAskEmail(true); }}
                    className="mt-3 inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">
                    <RefreshCw className="h-4 w-4" /> Activar pago automático
                  </button>
                )}
                <p className="mt-2 text-xs text-content-3">Necesitas una cuenta de Mercado Pago (se crea gratis al autorizar).</p>
              </>
            )}
            {autopayErr && <p className="mt-2 text-sm text-red-600">{autopayErr}</p>}
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2 sm:items-stretch">
          <div
            className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up transition-transform transition-shadow duration-300 transform hover:scale-[1.02] hover:shadow-lg flex flex-col"
            style={{ animationDelay: '0.15s', animationFillMode: 'both' }}
          >
            <div className="px-4 py-6 sm:px-6 sm:py-8 flex-grow">
              <h3 className="text-2xl font-bold text-content">Gratis</h3>
              <p className="mt-4 text-sm text-content-3">
                Ideal para comenzar y probar las funciones básicas
              </p>
              <p className="mt-8">
                <span className="text-4xl font-bold text-content">$0</span>
                <span className="text-content-3 ml-2">/mes</span>
              </p>
              <div className="mt-8 text-content-2">
                <FeatureList features={freeFeatures} iconColor="text-green-500" />
              </div>
            </div>
            <div className="px-4 sm:px-6 pb-6 sm:pb-8 pt-4">
              <button
                disabled={true}
                className="w-full py-2 px-4 rounded-md font-medium bg-canvas text-content cursor-not-allowed"
              >
                {!hasProAccess ? 'Plan Actual' : 'Plan Básico'}
              </button>
            </div>
          </div>

          <div
            className="bg-gradient-to-br from-blue-600 to-indigo-700 shadow rounded-lg overflow-hidden text-white relative animate-fade-in-up transition-transform transition-shadow duration-300 transform hover:scale-[1.02] hover:shadow-xl flex flex-col"
            style={{ animationDelay: '0.15s', animationFillMode: 'both' }}
          >
            <div className="px-4 py-6 sm:px-6 sm:py-8 relative z-10 flex-grow">
              <h3 className="text-2xl font-bold">Pro</h3>
              <p className="mt-4 text-sm text-blue-100">
                Funcionalidades avanzadas para potenciar tu negocio
              </p>
              <p className="mt-8">
                {/* Con plan de pago: lo que paga. En gratis: la opción más barata del servidor
                    («desde $208 al mes», 27-sep-2026) — antes enseñaba $2,500/año fijo. */}
                {!precio && opcionMensual && <span className="text-blue-200 text-sm mr-1">desde</span>}
                <span className="text-4xl font-bold">
                  ${(precio ? precio.monto : (opcionMensual ? opcionMensual.amount : 0)).toLocaleString('es-MX')}
                </span>
                <span className="text-blue-200 ml-2">{periodSuffixOf(precio ? precio.meses : (opcionMensual ? opcionMensual.months : 12))}</span>
              </p>
              {!precio && planOptions.length > 1 && (
                <p className="mt-1 text-xs text-blue-200">
                  {planOptions.filter((o) => o.code !== 'pro_monthly').map((o) => `$${o.amount.toLocaleString('es-MX')} ${o.label.toLowerCase()}`).join(' · ')} · tú eliges el periodo al contratar
                </p>
              )}
              <p className="mt-1 text-xs text-blue-200">
                {/* El desglose por sucursales es de la tarifa estándar de lealtad: a quien tiene
                    precio acordado (todas las clínicas y Karla) le confunde, porque su monto no
                    sale de ahí. Se le dice lo suyo: cada cuánto se le cobra. */}
                {esVerticalDePago
                  ? chargedEveryOf(precio?.meses)
                  : (Math.max(1, currentUser?.maxBranches ?? 1) > 1
                      ? `${currentUser?.maxBranches} sucursales · precio escalonado por volumen`
                      : 'por sucursal · 1 sucursal incluida')}
              </p>
              <div className="mt-8 text-blue-50">
                {/* Beneficios RICOS del servidor (plan L2): ícono + título + qué gana en concreto.
                    Sin lista rica (carga / backend viejo) → checklist plano de siempre. */}
                {featuresRich ? (
                  <ul className="space-y-3.5">
                    {featuresRich.map((f) => {
                      const Icon = FEATURE_ICONS[f.icon] || CheckCircle;
                      return (
                        <li key={f.title} className="flex items-start gap-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
                            <Icon size={16} className="text-white" />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-white">{f.title}</span>
                            <span className="mt-0.5 block text-xs leading-relaxed text-blue-100">{f.desc}</span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <FeatureList features={proFeatures} iconColor="text-blue-300" Icon={CheckCircle} />
                )}
              </div>
            </div>
            <div className="px-4 sm:px-6 pb-6 sm:pb-8 pt-4 z-10">
              <button
                className={
                  'w-full bg-white/20 text-white py-2 px-4 rounded-md font-medium transition-colors relative overflow-hidden ' +
                  (hasProAccess || hasPendingPayment ? 'opacity-70 cursor-not-allowed hover:bg-white/20' : 'hover:bg-white/30')
                }
                onClick={!hasProAccess && !hasPendingPayment ? handleSubscribe : undefined}
                disabled={hasProAccess || hasPendingPayment}
              >
                {hasProAccess ? (
                  <span className="flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 inline-block mr-2" /> Ya eres {currentPlanLabel}
                  </span>
                ) : hasPendingPayment ? (
                  <span className="flex items-center justify-center">
                    <Clock className="h-5 w-5 inline-block mr-2" /> Pago en revisión
                  </span>
                ) : (
                  <span className="flex items-center justify-center relative">
                    <span className="absolute inset-0 overflow-hidden">
                      
                    </span>
                    <CreditCard className="h-4 w-4 inline-block mr-2" />
                    Contratar Plan PRO
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Historial de pagos (plan L3): getAccount ya lo traía y solo se usaba el último.
            Un cliente con años pagando merece ver su registro sin escribirle a nadie. */}
        {historial.length > 0 && (
          <div className="bg-surface shadow rounded-lg p-4 sm:p-6 animate-fade-in-up" style={{ animationDelay: '0.15s', animationFillMode: 'both' }}>
            <h3 className="text-lg font-medium text-content mb-3">Mis pagos</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-content-3">
                    <th className="pb-2 pr-3 font-medium">Fecha</th>
                    <th className="pb-2 pr-3 font-medium">Monto</th>
                    <th className="pb-2 pr-3 font-medium">Forma</th>
                    <th className="pb-2 pr-3 font-medium">Cubre hasta</th>
                    <th className="pb-2 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map((p) => (
                    <tr key={p.id} className="border-t border-line">
                      <td className="py-2 pr-3 text-content-2">{p.createdAt ? p.createdAt.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                      <td className="py-2 pr-3 font-medium text-content">${p.amount.toLocaleString('es-MX')} {p.currency}</td>
                      <td className="py-2 pr-3 text-content-2">{p.method === 'mercadopago' ? 'Tarjeta' : p.method === 'btcpay' ? 'Cripto' : 'Transferencia'}</td>
                      <td className="py-2 pr-3 text-content-2">{p.periodEnd ? p.periodEnd.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                      <td className="py-2">
                        {p.status === 'completed'
                          ? <span className="text-emerald-600 dark:text-emerald-400">Pagado</span>
                          : <span className="text-blue-600 dark:text-blue-400">En proceso</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div
          className="bg-surface-2 rounded-lg p-4 sm:p-6 animate-fade-in-up"
          style={{ animationDelay: '0.15s', animationFillMode: 'both' }}
        >
          <h3 className="text-lg font-medium text-content mb-4">
            Preguntas Frecuentes
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-content">
                ¿Cómo funcionan las sucursales?
              </h4>
              <p className="mt-1 text-sm text-content-3">
                Todos los planes incluyen 1 sucursal. ¿Tienes más locales? El precio por sucursal <strong>baja por volumen</strong>: la 1ª a 3ª a $2,500/año cada una, de la 4ª a la 6ª $2,000, y de la 7ª en adelante $1,500 — entre más sucursales, más barata te sale cada una (NO se multiplica plano). Escríbenos y te las activamos en tu cuenta.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-content">
                ¿Cómo funciona el envío de mensajes por WhatsApp en el plan PRO?
              </h4>
              <p className="mt-1 text-sm text-content-3">
                El plan PRO te permite enviar mensajes directos a tus clientes usando plantillas prediseñadas para diferentes ocasiones (como cumpleaños, promociones, etc.), facilitando la comunicación directa y personalizada. Requiere configuración inicial.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-content">
                ¿Qué sucede cuando expira mi suscripción PRO?
              </h4>
              <p className="mt-1 text-sm text-content-3">
                Tu cuenta volverá automáticamente al plan Gratis con 1 sucursal activa. Mantendrás acceso a todos tus clientes y tus datos — el kiosco y los puntos siguen funcionando — pero perderás las funcionalidades exclusivas del plan PRO (mensajes automáticos por WhatsApp, campañas, sucursales adicionales, reportes avanzados, etc.). El plan gratis admite hasta 1,000 clientes registrados: si tienes más, se quedan, pero no podrás registrar nuevos hasta renovar.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-content">
                ¿Puedo pagar antes de que venza?
              </h4>
              <p className="mt-1 text-sm text-content-3">
                Sí. El pago se suma a tu vigencia actual: no pierdes ni un día por adelantarte.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-content">
                ¿Tarjeta, transferencia o cripto?
              </h4>
              <p className="mt-1 text-sm text-content-3">
                Con tarjeta (Mercado Pago) tu plan se activa al instante. Con criptomonedas, en cuanto la red confirma (unos minutos). Con transferencia, sube tu comprobante y lo activamos en máximo 24 horas hábiles.
              </p>
            </div>
          </div>
        </div>

        <div
          className="bg-surface p-4 rounded-lg border border-line text-center animate-fade-in-up"
          style={{ animationDelay: '0.15s', animationFillMode: 'both' }}
        >
          <div className="flex items-center justify-center">
            <Shield className="h-4 w-4 text-content-3 mr-2" />
            <p className="text-xs text-content-3">
              Pagos procesados de forma segura. La información de pago está encriptada.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default SubscriptionPage;